import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calculator, Building2, ShieldCheck, Briefcase, FileCheck, Users, Globe, ArrowRight } from 'lucide-react';

const Services = () => {
  const navigate = useNavigate();
  
  const services = [
    {
      id: 'direct-taxation',
      icon: Calculator,
      title: 'Direct Taxation',
      description: 'Comprehensive income tax planning, return filing, and tax advisory services for individuals and businesses to optimize tax liabilities.',
      features: ['Tax Planning', 'ITR Filing', 'Tax Assessment', 'TDS Services']
    },
    {
      id: 'indirect-taxation-gst',
      icon: FileText,
      title: 'Indirect Taxation & GST',
      description: 'Expert GST registration, return filing, compliance, and advisory services to navigate the complex indirect tax landscape.',
      features: ['GST Registration', 'GST Returns', 'Input Tax Credit', 'GST Audit']
    },
    {
      id: 'audit-assurance',
      icon: ShieldCheck,
      title: 'Audit & Assurance',
      description: 'Professional audit services including statutory audit, tax audit, internal audit, and other assurance services.',
      features: ['Statutory Audit', 'Tax Audit', 'Internal Audit', 'Bank Audit']
    },
    {
      id: 'business-setup',
      icon: Building2,
      title: 'Business Setup Services',
      description: 'End-to-end support for business registration, incorporation, and compliance for startups and established companies.',
      features: ['Company Formation', 'LLP Registration', 'Partnership Deed', 'Trade License']
    },
    {
      id: 'corporate-law',
      icon: FileCheck,
      title: 'Corporate Law Services',
      description: 'Complete corporate compliance solutions including ROC filings, board meetings, annual returns, and statutory registers.',
      features: ['ROC Compliance', 'Annual Filing', 'Board Meetings', 'Secretarial Audit']
    },
    {
      id: 'accounting-bookkeeping',
      icon: Briefcase,
      title: 'Accounting & Bookkeeping',
      description: 'Accurate and timely bookkeeping, financial statement preparation, and accounting advisory services.',
      features: ['Books Maintenance', 'Financial Statements', 'MIS Reports', 'Payroll Services']
    },
    {
      id: 'nri-services',
      icon: Globe,
      title: 'NRI Services',
      description: 'Specialized tax and compliance services for Non-Resident Indians including property transactions and fund repatriation.',
      features: ['NRI Tax Returns', 'FEMA Compliance', 'Property Sale', 'Repatriation']
    },
    {
      id: 'business-advisory',
      icon: Users,
      title: 'Business Advisory',
      description: 'Strategic financial advisory, business planning, funding assistance, and growth consulting services.',
      features: ['Business Planning', 'Funding Support', 'Valuation', 'Due Diligence']
    }
  ];

  return (
    <section id="services" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wide">Our Services</span>
          <h2 className="text-4xl font-bold text-slate-900 mt-3 mb-4">
            Comprehensive Financial Solutions
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From taxation to audit and business advisory, we offer end-to-end services to meet all your financial and compliance needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={index}
                onClick={() => navigate(`/services/${service.id}`)}
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-100 cursor-pointer"
              >
                <div className="bg-teal-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <IconComponent className="w-8 h-8 text-teal-700 group-hover:text-white transition-colors duration-300" />
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-teal-600 transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-slate-600 mb-4 leading-relaxed text-sm">
                  {service.description}
                </p>

                <div className="space-y-2 pt-4 border-t border-slate-100 mb-4">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Learn More Link */}
                <div className="flex items-center gap-2 text-teal-600 font-medium text-sm group-hover:gap-3 transition-all">
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
